import styled, { css } from 'styled-components';
import { themeGet } from '@styled-system/theme-get';

import Container from '../../Core/Container';
import heart from '../../../images/pc-heart.png';
import underline from '../../../images/underline.svg';

export const Section = styled.section`
  background-color: ${({ theme, backgroundColor }) =>
    backgroundColor ? theme.colors[backgroundColor] : ''};

  &.pcma-list-section {
    background-color: transparent;
    @media only screen and (min-width: ${({ theme }) => theme.breakpoints.lg}) {
      width: 50%;
    }
  }

  &.claims-rewards-section {
    padding: 60px 0;

    @media only screen and (min-width: ${({ theme }) => theme.breakpoints.md}) {
      padding-bottom: 70px;
    }
  }
`;

export const Grid = styled(Container)`
  background-color: ${({ bgColor }) =>
    bgColor ? themeGet(`colors.${bgColor}`) : 'inherit'};

  ${Section}.claims-rewards-section & {
    margin: 0;
    width: 100%;
    @media only screen and (min-width: ${({ theme }) => theme.breakpoints.md}) {
      margin: 0 auto;
    }
  }

  ${Section}.pcma-list-section & {
    width: 100%;
  }
`;

export const Intro = styled.div`
  margin-bottom: 35px;
  position: relative;

  h2 {
    text-align: center;
    margin-bottom: 10px;
  }

  p {
    font-weight: normal;
    font-size: 16px;
    line-height: 24px;
    text-align: center;
  }

  @media only screen and (max-width: calc(${themeGet(
      'breakpoints.md',
    )} - 1px)) {
    h2,
    p {
      br {
        display: none;
      }
    }
  }

  @media only screen and (min-width: ${({ theme }) => theme.breakpoints.xlg}) {
    margin-bottom: 60px;

    h2 {
      text-align: center;
      margin-bottom: 35px;
    }

    p {
      font-size: 18px;
      line-height: 30px;
    }
  }

  ${Section}.claims-rewards-section & {
    margin: 0 30px 40px 30px;

    h2 {
      color: ${({ theme }) => theme.colors.velvet};
      line-height: 36px;

      span.pc-heart {
        position: relative;
        font-size: inherit;
        line-height: inherit;
        &:after {
          content: '';
          display: block;
          background-image: url(${heart});
          background-size: contain;
          background-position: center;
          background-repeat: no-repeat;
          height: 58px;
          width: auto;
          margin-top: 10px;

          @media only screen and (min-width: ${themeGet('breakpoints.md')}) {
            position: absolute;
            height: 55px;
            width: 43px;
            bottom: -30%;
            left: 15px;
            margin-top: unset;
          }

          @media only screen and (min-width: ${themeGet('breakpoints.lg')}) {
            height: 58px;
            width: 45px;
            bottom: -30%;
          }

          @media only screen and (min-width: ${themeGet('breakpoints.xlg')}) {
            width: 82px;
          }
        }
      }
    }

    @media only screen and (min-width: ${({ theme }) => theme.breakpoints.md}) {
      max-width: 1084px;
      margin: 0 auto;
      margin-bottom: 67px;

      h2 {
        font-size: 2.25rem;
        line-height: 130%;
      }
    }
  }
`;

export const Card = styled.div`
  background-color: ${({ theme }) => theme.colors.white};
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
  padding: 12px 24px;
  min-height: 165px;

  &:first-child {
    padding-top: 24px;
    @media only screen and (min-width: ${({ theme }) => theme.breakpoints.md}) {
      padding-top: 40px;
    }
    @media only screen and (min-width: ${({ theme }) => theme.breakpoints.lg}) {
      padding-top: 55px;
    }
  }

  ${({ icon }) =>
    icon &&
    css`
      &::before {
        content: '';
        background: center / contain no-repeat url(${icon});
        width: fit-content;
        max-width: 56px;
        max-height: 56px;
        width: 100%;
        height: 56px;
        margin-right: 16px;
      }
    `}

  @media only screen and (min-width: ${({ theme }) => theme.breakpoints.sm}) {
    &::before {
      margin-right: 12.5px;
    }
  }

  @media only screen and (min-width: ${({ theme }) => theme.breakpoints.md}) {
    min-height: 135px;
    padding: 8px 36px;
    ${({ icon }) =>
      icon &&
      css`
        &::before {
          max-width: 70px;
          max-height: 70px;
          width: 100%;
          height: 70px;
          margin-right: 16px;
        }
      `}
  }

  @media only screen and (min-width: ${({ theme }) => theme.breakpoints.lg}) {
    padding: 22px 36px;
  }

  @media only screen and (min-width: ${({ theme }) => theme.breakpoints.xlg}) {
    padding: 16px 44px;
  }

  ${Section}.pcma-list-section & {
    min-height: unset;
  }

  ${Section}.claims-rewards-section & {
    flex-flow: column nowrap;
    align-items: start;
    padding: 20px 30px;
    min-height: 130px;
    border-radius: 20px;

    &::before {
      max-width: 120px;
      max-height: 31px;
      height: 31px;
      margin-bottom: 15px;
      background-position-x: left;
    }

    &::after {
      display: none;
    }

    @media only screen and (min-width: ${({ theme }) => theme.breakpoints.md}) {
      padding: 38px;
      padding-right: 0;
      min-height: 163px;
      flex-flow: row nowrap;
      align-items: center;

      &::before {
        max-width: 180px;
        max-height: 64px;
        height: 64px;
        margin-right: 34px;
        margin-bottom: 0;
        background-position-x: center;
      }
    }
  }
`;

export const Cards = styled.div`
  display: grid;

  ${Section}.claims-rewards-section & {
    padding-top: 0px;
    grid-gap: 30px 0px;

    > ${Card}:nth-child(2n-1):nth-last-of-type(1) {
      grid-column: span 1;
      text-align: center;
      p {
        font-size: 1rem;
        line-height: 150%;
        letter-spacing: 0.75px;
      }
    }

    @media only screen and (min-width: ${({ theme }) => theme.breakpoints.md}) {
      grid-template-columns: repeat(2, 1fr);
      grid-gap: 27px 20px;

      > ${Card}:nth-child(2n-1):nth-last-of-type(1) {
        grid-column: span 2;
        padding: 38px 172px;
      }
    }
  }
`;

export const Content = styled.div`
  position: relative;
  text-align: left;

  h3 {
    font-size: 1rem;
    line-height: 22px;
    font-weight: ${({ theme }) => theme.fontWeights.medium};

    span {
      font: inherit;
    }

    span.red {
      color: ${({ theme }) => theme.colors['pc-red']};
    }

    @media only screen and (min-width: ${({ theme }) => theme.breakpoints.md}) {
      font-size: 1.25rem;
    }
  }

  p {
    font-size: 16px;
    line-height: 20px;
  }

  a,
  span {
    font-size: inherit;
    font-weight: inherit;
  }

  span.pc-underline {
    display: inline-flex;
    flex-direction: column;
    &:after {
      display: block;
      content: '';
      height: 10px;
      width: 42px;
      margin: 0px auto -9px;
      background-image: url(${underline});
      background-repeat: no-repeat;
      background-position: center;
      background-size: contain;

      @media only screen and (min-width: ${({ theme }) =>
          theme.breakpoints.md}) {
        height: 10px;
        width: 42px;
      }
    }
  }

  @media only screen and (min-width: ${({ theme }) => theme.breakpoints.xlg}) {
    h3 {
      font-size: 20px;
      line-height: 30px;
    }

    p {
      font-size: 16px;
      line-height: 22px;
    }
  }

  ${Section}.claims-rewards-section & {
    margin-right: 8px;
    p {
      font-size: 1rem;
      line-height: 150%;
      font-weight: ${({ theme }) => theme.fontWeights.medium};
    }
    @media only screen and (min-width: ${({ theme }) => theme.breakpoints.md}) {
      margin-right: 15px;
    }
  }
`;

export const Footer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;

  a {
    margin: 10px auto 3rem;
  }

  ${Section}.claims-rewards-section & {
    display: none;
  }
`;

export const SectionDisclaimerWrapper = styled.div`
  margin: 0 30px;
  max-width: 1084px;
  p {
    font-size: 1rem;
    line-height: 150%;
  }
  @media only screen and (min-width: ${({ theme }) => theme.breakpoints.lg}) {
    margin: 0 auto;
  }
`;
